import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import Datatable from "@components/datatable";
import StatusChip from "@components/ui-components/status-chip";
import DebitNotePreviewModal from "@panel/shared/debit-note/components/debit-note-preview-modal";
import useDebitNotePreview from "@panel/shared/debit-note/hooks/use-debit-note-preview";
import RevertDebitNoteModal from "@src/views/panel/ops/brands/details/shared/revert-debit-note-modal";
import DebitNoteActions from "@src/views/panel/ops/brands/details/debit-notes/components/debit-note-actions";
import { STATUS_CAPTION } from "@src/views/panel/ops/brands/details/debit-notes/misc/constants";
import useDebitNotesList from "./hooks/use-debit-notes-list";
import usePreviewBrand from "./hooks/use-preview-brand";
import { formatCurrency } from "@src/utility/helpers/utils";
import { DEBIT_NOTE_STATUS_LABEL, DEBIT_NOTE_STATUS_TONE } from "@src/utility/constants/panel/common";
import { formatDate } from "@src/utility/helpers/date";

const DebitNotesList = () => {
    const {
        items, pagination, loading,
        handlePageChange, handlePerRowsChange,
        revertTarget, openRevert, closeRevert,
        revertNote, markAsReceived, issueToBrand, fetchInvoices
    } = useDebitNotesList();
    const preview = useDebitNotePreview({ notes: items, fetchInvoices });
    const { brand, loadBrand } = usePreviewBrand();

    const openPreview = (note) => {
        loadBrand(note);
        preview.openPreview(note);
    };

    const columns = [
        { Header: "DN No", accessor: "debit_note_number", Cell: ({ value }) => <span className="font-mono text-sm font-medium text-gray-900">{value}</span> },
        {
            Header  : "Brand",
            accessor: "account_name",
            Cell    : ({ value, row }) => (
                // Stop the click here so following the link doesn't also open the preview.
                <Link to={`/brands/details/${row.account_id}`} onClick={(e) => e.stopPropagation()} className="text-gray-900 hover:text-accent hover:underline">
                    {value}
                </Link>
            )
        },
        { Header: "Period", accessor: "payout_period", Cell: ({ value }) => <span className="text-gray-700">{value ? formatDate(value) : "—"}</span> },
        {
            Header  : "Status",
            accessor: "status",
            Cell    : ({ value }) => <StatusChip tone={DEBIT_NOTE_STATUS_TONE[value] || "neutral"} label={DEBIT_NOTE_STATUS_LABEL[value] || value} />
        },
        {
            Header  : "Total",
            accessor: "amount",
            Cell    : ({ value, row }) => <span className="font-medium text-gray-900">{formatCurrency(value, row.currency)}</span>
        },
        {
            Header  : "Invoices",
            accessor: "paid_invoices",
            Cell    : ({ value, row }) => <span className="text-gray-700">{value} / {row.total_invoices} paid</span>
        },
        {
            Header  : "",
            accessor: "_id",
            Cell    : ({ row }) => (
                <div className="flex items-center justify-end gap-3">
                    {/* Quick actions without opening the preview, so stop the row click here. */}
                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                        <DebitNoteActions
                            note={row}
                            onIssue={issueToBrand}
                            onRevert={openRevert}
                            onMarkAsReceived={markAsReceived} />
                    </div>
                    <span className="flex items-center gap-1 text-sm font-medium text-accent">
                        View
                        <ChevronRight className="h-3.5 w-3.5" />
                    </span>
                </div>
            )
        }
    ];

    return (
        <div className="p-6">
            <h1 className="text-2xl font-semibold tracking-tight">All debit notes</h1>
            <p className="mt-1 text-sm text-muted">Every cycle across all brands.</p>

            <div className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <Datatable
                    columns={columns}
                    data={items}
                    loading={loading}
                    pagination={{ count: pagination.total, page: pagination.page, limit: pagination.limit }}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                    onRowClicked={openPreview}
                    customNoData={<span className="text-sm text-muted">No debit notes yet.</span>} />
            </div>

            <DebitNotePreviewModal
                preview={preview}
                brand={brand}
                caption={STATUS_CAPTION[preview.previewNote?.status]}
                actions={preview.previewNote && (
                    <DebitNoteActions
                        note={preview.previewNote}
                        onIssue={issueToBrand}
                        onRevert={openRevert}
                        onMarkAsReceived={markAsReceived} />
                )} />

            <RevertDebitNoteModal
                open={Boolean(revertTarget)}
                onClose={closeRevert}
                debitNoteLabel={revertTarget?.debit_note_number || ""}
                onRevert={(reason) => revertNote(revertTarget._id, reason)} />
        </div>
    );
};

export default DebitNotesList;
